// src/components/Projects.js
import { useEffect, useRef } from "react";
import { FaExternalLinkAlt, FaGooglePlay } from "react-icons/fa";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const projects = [
  {
    name: "SUBG QUIZ",
    initials: "SQ",
    tagline: "Play Quizzes, Level Up, Get Certificates",
    desc: "A subscription-based quiz platform with multi-level quizzes, free & premium content for all age groups.",
    webLink: "https://subgquiz.com",
    appLink: "https://play.google.com/store/apps/details?id=com.subgapp",
    gradient: "from-indigo-500 to-purple-600",
  },
  {
    name: "AAJEXAM",
    initials: "AE",
    tagline: "Prepare Your Exam Today",
    desc: "A mock test & exam preparation platform with Practice Tests, PYQs, Quizzes and performance tracking.",
    webLink: "https://aajexam.com",
    appLink: "https://play.google.com/store/apps/details?id=com.aajexam.app",
    gradient: "from-orange-500 to-rose-600",
  },
];

export default function Projects() {
  const ref = useRef();

  useEffect(() => {
    gsap.fromTo(
      ref.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        scrollTrigger: {
          trigger: ref.current,
          start: "top 80%",
        },
      }
    );
  }, []);

  return (
    <section
      id="products"
      ref={ref}
      className="relative min-h-screen px-6 py-20 bg-white border-t border-gray-200 overflow-hidden flex items-center"
    >
      <div className="pointer-events-none absolute top-0 left-1/4 w-72 h-72 bg-blue-100/60 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-80 h-80 bg-indigo-100/60 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-5xl mx-auto w-full">
        <div className="text-center mb-10">
          <span className="inline-block text-xs md:text-sm font-semibold tracking-widest uppercase text-indigo-700 bg-indigo-50 px-4 py-1.5 rounded-full shadow-sm mb-4">
            What I've built
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">
            My Products
          </h2>
          <p className="text-center text-gray-600">
            Two products I've built and grown end-to-end — web &amp; mobile.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {projects.map((project, idx) => (
            <div
              key={idx}
              className="group relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden flex flex-col border border-gray-100"
            >
              <div className={`h-2 bg-gradient-to-r ${project.gradient}`} />

              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-4 mb-3">
                  <div
                    className={`w-12 h-12 shrink-0 rounded-xl flex items-center justify-center text-white font-bold text-lg bg-gradient-to-br ${project.gradient} shadow-md group-hover:scale-110 transition`}
                  >
                    {project.initials}
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">
                      {project.name}
                    </h3>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      Live
                    </span>
                  </div>
                </div>
                <p
                  className={`text-sm font-medium mb-3 bg-gradient-to-r ${project.gradient} bg-clip-text text-transparent`}
                >
                  {project.tagline}
                </p>
                <p className="text-gray-600 text-sm mb-6 flex-1">
                  {project.desc}
                </p>

                <div className="flex flex-wrap gap-3">
                  <a
                    href={project.webLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full text-white bg-gradient-to-r ${project.gradient} shadow-sm hover:shadow-md hover:scale-[1.03] transition`}
                  >
                    Visit Website <FaExternalLinkAlt size={12} />
                  </a>
                  <a
                    href={project.appLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full text-gray-800 border border-gray-300 hover:bg-gray-100 transition"
                  >
                    <FaGooglePlay /> Get the App
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
